import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { SimpleSearch, SimpleSearchWithId } from '../../reduxToolkit/Search/SearchApi';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import getTimezoneOffset from '../../utils/GetTimeZone';
import CustomSearch from './CustomSearch';
import {SEARCH_MODEL} from "../table/Model/TableModel";
/**
 * @component SearchSimple
 * @description Handles a simple search logic with optional filters for contractors, suppliers, and attendance data.
 *
 * @param {string} id - ID for the search input.
 * @param {Function} setSearchElement - Setter for the search input value.
 * @param {string} searchElement - Current value in the search input.
 * @param {string} optionTable - Target model name for the backend search.
 * @param {number} rowsPerPage - Number of rows per page.
 * @param {Function} setBodyForExport - Setter for export body payload (optional).
 * @param {string} attendanceStartDate - Optional filter for attendance.
 * @param {string} attendanceEndDate - Optional filter for attendance.
 * @param {string} attendanceSearchType - Optional filter for attendance type.
 * @param {Function} setSimpleSearch - Flag setter to indicate if a simple search was executed.
 * @param {boolean} searchClean - Flag that triggers reset behavior.
 * @param {Function} setSearch - Setter for general search status.
 * @param {Function} setSearchResults - Setter for search result data.
 * @param {string|null} contractorId - Optional contractor ID for scoped searches.
 * @param {string|null} supplierId - Optional supplier ID for scoped searches.
 * @param {Function|null} setSendSimpleSearch - Setter flag to trigger search externally.
 * @param {string|null} viewType - Optional filter value used in the request body.
 * @param {Function|null} callApi - Optional override to call a custom API handler.
 * @param {Function|null} resetApi - Optional override to call a custom reset handler.
 * @param {string} [label='type_something_to_search'] - Optional translation key or text for the search input label.
 * @param userTypeIds [userTypeIds] - Optional list to ids for the simple search, case for contractor and supplier are null
 */
const SearchSimple = ({
                          id,
                          setSearchElement,
                          searchElement,
                          optionTable,
                          rowsPerPage,
                          setBodyForExport = () => {},
                          attendanceStartDate,
                          attendanceEndDate,
                          attendanceSearchType,
                          setSimpleSearch,
                          searchClean,
                          setSearch,
                          setSearchResults,
                          contractorId,
                          supplierId,
                          setSendSimpleSearch,
                          viewType,
                          callApi=null,
                          resetApi = null,
                          label='type_something_to_search',
                          userTypeIds
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    /**
     * Triggers the search logic based on current input and filters.
     * Dispatches the appropriate API request depending on contractor/supplier context.
     */
    const filterSectionByElement = (search) => {
        if (search.trim()) {
            if(callApi != null){
                callApi();
                return;
            }
            if (contractorId === null && supplierId === null) {
                let body;
                if (attendanceStartDate || attendanceEndDate || attendanceSearchType) {
                    body = SEARCH_MODEL.setObject({
                        page:0,
                        size:rowsPerPage,
                        model:optionTable,
                        key:search,
                        attendanceStartDate,
                        attendanceTimeZone: getTimezoneOffset(),
                        attendanceEndDate,
                        attendanceSearchType
                    });
                } else {
                    body = SEARCH_MODEL.setObject({page:0, size:rowsPerPage, model:optionTable, key:search});
                }
                if (viewType !== null && viewType !== undefined) {
                    body.viewType = viewType;
                }
                if (userTypeIds !== null && userTypeIds !== undefined){
                    body.userTypeIds = userTypeIds;
                }
                setBodyForExport(body);
                dispatch(SimpleSearch({ body })).then(
                    ({
                        payload: {
                            data: { data, success },
                        },
                    }) => {
                        setDataResults(success, data);
                    },
                );
            } else {
                const body = SEARCH_MODEL.setObject({page:0, size:rowsPerPage, model:optionTable, key:search, userId: contractorId ? contractorId : supplierId});

                if (viewType !== null && viewType !== undefined) {
                    body.viewType = viewType;
                }
                setBodyForExport(body);
                dispatch(SimpleSearchWithId({ body })).then(
                    ({
                        payload: {
                            data: { data, success },
                        },
                    }) => {
                        {
                            setDataResults(success, data);
                        }
                    },
                );
            }
        }
    };

    // Update local state when input changes
    const handleChange = (e) => {
        const newValue = e.target.value;
        setSearchElement(newValue);

        if (newValue === '') {
            handleReset();
        }
    };

    // Trigger reset when `searchClean` flag is set externally
    useEffect(() => {
        if (searchClean) {
            handleReset();
        }
    }, [searchClean]);

    /**
     * Resets the search results and optionally refetches default data.
     */
    const handleReset = () => {
        setSearchResults(null);
        setBodyForExport('');
        setSimpleSearch(false);

        if(resetApi != null){
            resetApi();
            return;
        }

        if (contractorId === null && supplierId === null) {
            let body;
            if (attendanceStartDate || attendanceEndDate || attendanceSearchType) {
                body = SEARCH_MODEL.setObject({
                    page:0,
                    size:rowsPerPage,
                    model:optionTable,
                    key:"",
                    attendanceStartDate,
                    attendanceTimeZone: getTimezoneOffset(),
                    attendanceEndDate,
                    attendanceSearchType
                });
            } else {
                body = SEARCH_MODEL.setObject({
                    page:0,
                    size:rowsPerPage,
                    model:optionTable,
                    key:"",
                });
            }
            if (viewType !== null && viewType !== undefined) {
                body.viewType = viewType;
            }
            if (userTypeIds !== null && userTypeIds !== undefined){
                body.userTypeIds = userTypeIds;
            }
            dispatch(SimpleSearch({ body })).then(
                ({
                    payload: {
                        data: { data, success },
                    },
                }) => {
                    setDataResults(success, data);
                    setBodyForExport(body);
                },
            );
        } else {
            const body = SEARCH_MODEL.setObject({
                page:0,
                size:rowsPerPage,
                model:optionTable,
                key:"",
                userId: contractorId ? contractorId : supplierId
            });

            if (viewType !== null && viewType !== undefined) {
                body.viewType = viewType;
            }
            dispatch(SimpleSearchWithId({ body })).then(
                ({
                    payload: {
                        data: { data, success },
                    },
                }) => {
                    {
                        setSearchElement('');
                        setSearchResults(data);
                    }
                },
            );
        }
    };

    /**
     * Helper function to handle result success or failure.
     */
    const setDataResults = (success, data) => {
        if (success) {
            setSearch(false);
            setSearchResults(data);
            setSimpleSearch(true);
            toast.success(t('search_completed_successfully'));
        } else {
            setSearchResults(null);
            toast.error(t('error_when_searching'));
        }
    }

    return (
        <form className='centerElements' onSubmit={(e) => e.preventDefault()}>
            <section className='searchElementByWord-devices--section'>
                <CustomSearch
                    id={id}
                    label={label}
                    value={searchElement}
                    setValue={setSearchElement}
                    onChange={handleChange}
                    onSearchIcon={() => filterSectionByElement(searchElement)}
                    searchFunction={() => filterSectionByElement(searchElement)}
                    onReset={handleReset}
                    setSendSimpleSearch={setSendSimpleSearch}
                />
            </section>
        </form>
    );
};

export default SearchSimple;
